import React, { useState } from 'react';
import { Modal } from '../ui/modal';
import { Button } from '../ui/button';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { RegistrationData, TicketType } from '../../lib/api';
import { formatPhoneNumber } from '../../lib/utils';

interface VisitorInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  registrationData: RegistrationData | null;
  onMarkAsEntered: (data: RegistrationData, ticketType: TicketType) => void;
  isProcessing?: boolean;
}

/**
 * 관람객 정보 모달 컴포넌트
 * QR코드 스캔 후 관람객의 상세 정보를 보여주고 입장처리를 할 수 있는 모달입니다.
 */
const VisitorInfoModal: React.FC<VisitorInfoModalProps> = ({
  isOpen,
  onClose,
  registrationData,
  onMarkAsEntered,
  isProcessing = false,
}) => {
  // 티켓 타입 선택 상태
  const [selectedTicketType, setSelectedTicketType] = useState<TicketType>('일반');
  
  // 관람객 정보가 없으면 아무것도 렌더링하지 않음
  if (!registrationData) {
    return null;
  }

  console.log('🔍 [VisitorInfoModal] 받은 등록 데이터:', registrationData);
  console.log('📧 [VisitorInfoModal] 이메일 데이터:', registrationData.email);
  console.log('🌐 [VisitorInfoModal] 국적 데이터:', registrationData.nationality);
  console.log('🎪 [VisitorInfoModal] 전시회 타입:', registrationData.exhibition_type);

  // 성별 한글 변환
  const getGenderText = (gender: string) => {
    switch (gender) {
      case 'male': return '남성';
      case 'female': return '여성';
      case 'other': return '기타';
      default: return gender;
    }
  };

  // 연령대 한글 변환
  const getAgeGroupText = (ageGroup: string) => {
    switch (ageGroup) {
      case 'teen': return '10대';
      case 'twenties': return '20대';
      case 'thirties': return '30대';
      case 'forties': return '40대';
      case 'fifties': return '50대';
      case 'sixties_plus': return '60대 이상';
      default: return ageGroup;
    }
  };

  // 지역 표시 이름 변환
  const getRegionDisplayName = (region: string) => {
    switch (region) {
      case 'seoul': return '서울';
      case 'busan': return '부산';
      case 'daegu': return '대구';
      case 'incheon': return '인천';
      case 'gwangju': return '광주';
      case 'daejeon': return '대전';
      case 'ulsan': return '울산';
      case 'sejong': return '세종';
      case 'gyeonggi': return '경기도';
      case 'gangwon': return '강원도';
      case 'chungbuk': return '충청북도';
      case 'chungnam': return '충청남도';
      case 'jeonbuk': return '전라북도';
      case 'jeonnam': return '전라남도';
      case 'gyeongnam': return '경상남도';
      case 'gyeongbuk': return '경상북도';
      case 'jeju': return '제주도';
      default: return region;
    }
  };

  // 날짜 포맷팅
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '미입력';
    return new Date(dateString).toLocaleString('ko-KR');
  };

  // 입장처리 핸들러
  const handleMarkAsEntered = () => {
    onMarkAsEntered(registrationData, selectedTicketType);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title="관람객 정보 확인"
    >
      <div className="p-6">
        <div className="space-y-6">
          {/* 개인정보 */}
          <div className="grid grid-cols-2 gap-4 py-4 border-b">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">이름</span>
              <span className="font-medium">{registrationData.name}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">전화번호</span>
              <span className="font-medium">{formatPhoneNumber(registrationData.phone)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">이메일</span>
              <span className="font-medium">{registrationData.email || '미입력'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">국적</span>
              <span className="font-medium">
                {registrationData.nationality === 'foreigner' ? '외국인 (Foreigner)' : 
                 registrationData.nationality === 'domestic' ? '내국인' : '미설정'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">성별</span>
              <span className="font-medium">{registrationData.gender === 'male' ? '남성' : '여성'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">연령대</span>
              <span className="font-medium">
                {registrationData.age_group === 'teen' && '10대'}
                {registrationData.age_group === 'twenties' && '20대'}
                {registrationData.age_group === 'thirties' && '30대'}
                {registrationData.age_group === 'forties' && '40대'}
                {registrationData.age_group === 'fifties' && '50대'}
                {registrationData.age_group === 'sixties_plus' && '60대 이상'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">지역</span>
              <span className="font-medium">{getRegionDisplayName(registrationData.region)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">전시회</span>
              <span className="font-medium">
                {registrationData.exhibition_type === 'urbanbreak' ? 'UrbanBreak' : 'ToyConvention 토이컨벤션'}
              </span>
            </div>
          </div>

          {/* 등록 정보 */}
          <div className="py-4 border-b">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-700">등록일시</span>
              <span className="font-medium">{formatDate(registrationData.created_at)}</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-700">QR 코드</span>
              <span className="font-mono text-sm">{registrationData.registration_id}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">입장 상태</span>
              <span className={`px-2 py-1 text-xs rounded-full ${
                registrationData.is_entered 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {registrationData.is_entered ? '입장 완료' : '입장 대기'}
              </span>
            </div>
          </div>

          {/* 동의 정보 */}
          <div className="py-4">
            <div className="grid grid-cols-1 gap-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-700">개인정보 수집 및 이용 동의</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  registrationData.privacy_consent ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {registrationData.privacy_consent ? '동의' : '미동의'}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-700">개인정보 제3자 제공 동의</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  registrationData.third_party_consent ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {registrationData.third_party_consent ? '동의' : '미동의'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-700">차기 전시회 관련 안내 문자 수신 동의</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  registrationData.sms_consent ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {registrationData.sms_consent ? '동의' : '미동의'}
                </span>
              </div>
            </div>
          </div>

          {/* 티켓 타입 선택 (입장 처리가 안된 경우에만 표시) */}
          {!registrationData.is_entered && (
            <div className="py-4 border-t">
              <h4 className="text-sm font-medium text-gray-900 mb-3">티켓 타입 선택</h4>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="ticketType"
                    value="일반"
                    checked={selectedTicketType === '일반'}
                    onChange={(e) => setSelectedTicketType(e.target.value as TicketType)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">일반</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="ticketType"
                    value="올데이"
                    checked={selectedTicketType === '올데이'}
                    onChange={(e) => setSelectedTicketType(e.target.value as TicketType)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">올데이</span>
                </label>
              </div>
            </div>
          )}

          {/* 버튼 영역 */}
          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isProcessing}
            >
              취소
            </Button>
            {!registrationData.is_entered && (
              <Button
                onClick={handleMarkAsEntered}
                disabled={isProcessing}
                className="bg-green-600 hover:bg-green-700"
              >
                {isProcessing ? '처리 중...' : '입장 처리'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default VisitorInfoModal; 